import { useState, useEffect } from "react";
import axios from "axios";
import { 
  ChevronRight, 
  ChevronDown, 
  Users, 
  Eye,
  DollarSign,
  ShieldAlert,
  Lightbulb,
  TrendingUp,
  AlertTriangle
} from "lucide-react";
import { API } from "@/utils/constants";
import { formatFullCurrency, formatCurrency, getViolationTypeLabel, getPeriodAbbrev } from "@/utils/helpers";
import InfoTooltip from "@/components/common/InfoTooltip";
import RuleDetailModal from "@/components/modals/RuleDetailModal";
import SavingsDetailModal from "@/components/modals/SavingsDetailModal";
import DamagesDetailModal from "@/components/modals/DamagesDetailModal";

const InsightsPage = () => {
  const [insights, setInsights] = useState(null);
  const [damages, setDamages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('annual');
  const [selectedYear, setSelectedYear] = useState('2026');
  const [expandedDamagesRules, setExpandedDamagesRules] = useState({});
  const [expandedSavingsRules, setExpandedSavingsRules] = useState({});
  const [selectedRuleId, setSelectedRuleId] = useState(null);
  const [savingsDetailRule, setSavingsDetailRule] = useState(null);
  const [savingsDetailLoading, setSavingsDetailLoading] = useState(false);
  const [savingsDetailData, setSavingsDetailData] = useState(null);
  const [damagesDetailRule, setDamagesDetailRule] = useState(null);
  const [damagesDetailLoading, setDamagesDetailLoading] = useState(false);
  const [damagesDetailData, setDamagesDetailData] = useState(null);
  const [showPenalties, setShowPenalties] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [insightsRes, damagesRes] = await Promise.all([
          axios.get(`${API}/compliance/insights`),
          axios.get(`${API}/compliance/damages`)
        ]);
        setInsights(insightsRes.data);
        setDamages(damagesRes.data);
      } catch (e) {
        console.error('Failed to fetch insights:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const fetchSavingsDetail = async (ruleId) => {
    setSavingsDetailLoading(true);
    setSavingsDetailRule(ruleId);
    try {
      const response = await axios.get(`${API}/compliance/insights/rule/${ruleId}`);
      setSavingsDetailData(response.data);
    } catch (e) {
      console.error('Failed to fetch savings detail:', e);
      setSavingsDetailData(null);
    } finally {
      setSavingsDetailLoading(false);
    }
  };

  const fetchDamagesDetail = async (ruleId) => {
    setDamagesDetailLoading(true);
    setDamagesDetailRule(ruleId);
    try {
      const response = await axios.get(`${API}/compliance/damages/rule/${ruleId}`);
      setDamagesDetailData(response.data);
    } catch (e) {
      console.error('Failed to fetch damages detail:', e);
      setDamagesDetailData(null);
    } finally {
      setDamagesDetailLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2EC4B6]"></div>
      </div>
    );
  }

  if (!insights) return null;

  const { cost_savings } = insights;

  // Calculate totals for display
  const getDamagesAmount = () => {
    if (!damages) return 0;
    const yearData = damages.totals?.[selectedYear];
    const periodDivisor = selectedPeriod === 'annual' ? 1 : (selectedPeriod === 'quarterly' ? 4 : 12);
    return showPenalties 
      ? (yearData?.damages_max || 0) / periodDivisor
      : (yearData?.back_wages || 0) / periodDivisor;
  };

  const getSavingsAmount = () => {
    const yearData = selectedYear === '2025' 
      ? cost_savings.totals?.last_year_2025 
      : cost_savings.totals?.projected_2026;
    return yearData?.[selectedPeriod] || 0;
  };

  const damagesAmount = getDamagesAmount();
  const savingsAmount = getSavingsAmount();

  const periodDivisor = selectedPeriod === 'annual' ? 1 : (selectedPeriod === 'quarterly' ? 4 : 12);

  // Sort damages rules by amount (highest first)
  const damagesRules = [...(damages?.rules || [])].sort((a, b) => {
    const yearDataA = a[`damages_${selectedYear}`];
    const yearDataB = b[`damages_${selectedYear}`];
    const amountA = showPenalties 
      ? (yearDataA?.total_damages_max || 0) / periodDivisor
      : (yearDataA?.back_wages || 0) / periodDivisor;
    const amountB = showPenalties 
      ? (yearDataB?.total_damages_max || 0) / periodDivisor
      : (yearDataB?.back_wages || 0) / periodDivisor;
    return amountB - amountA; // Descending order
  });

  // Sort savings rules by amount (highest first)
  const savingsRules = [...(cost_savings?.rules || [])].sort((a, b) => {
    const amountA = a[`savings_${selectedYear}_${selectedPeriod}`] || 0;
    const amountB = b[`savings_${selectedYear}_${selectedPeriod}`] || 0;
    return amountB - amountA; // Descending order
  });

  // Calculate max for progress bars
  const getMaxDamagesAmount = () => {
    return Math.max(...damagesRules.map(r => {
      const yearData = r[`damages_${selectedYear}`];
      return showPenalties 
        ? (yearData?.total_damages_max || 0) / periodDivisor
        : (yearData?.back_wages || 0) / periodDivisor;
    }), 1);
  };

  const getMaxSavingsAmount = () => {
    return Math.max(...savingsRules.map(r => r[`savings_${selectedYear}_${selectedPeriod}`] || 0), 1);
  };

  const maxDamagesAmount = getMaxDamagesAmount();
  const maxSavingsAmount = getMaxSavingsAmount();

  return (
    <div data-testid="insights-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-[#2EC4B6]/20">
          <Lightbulb className="w-5 h-5 text-[#2EC4B6]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-white">Compliance Insights</h1>
          <p className="text-xs text-slate-400">Risk analysis and cost optimization</p>
        </div>
      </div>

      {/* Controls Row */}
      <div className="flex items-center justify-center gap-4 flex-wrap">
        {/* Penalties Toggle */}
        <button
          onClick={() => setShowPenalties(!showPenalties)}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
            showPenalties 
              ? 'bg-red-900/40 border-red-500 text-red-300 shadow-lg shadow-red-500/25' 
              : 'bg-slate-800 border-slate-600 text-slate-400 hover:text-white hover:border-slate-500'
          }`}
          data-testid="show-penalties-toggle"
        >
          <div className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-all duration-200 ${
            showPenalties 
              ? 'bg-red-500 border-red-400 scale-110' 
              : 'bg-slate-700/50 border-slate-500'
          }`}>
            {showPenalties ? (
              <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <div className="w-2 h-2 rounded-sm bg-slate-500/30"></div>
            )}
          </div>
          <span className={`text-sm font-medium whitespace-nowrap transition-all ${
            showPenalties ? 'text-red-300' : ''
          }`}>
            Include Penalties
          </span>
          <InfoTooltip text="Toggle to include or exclude civil penalties and liquidated damages from totals. Penalties can significantly increase exposure amounts." />
        </button>
        
        {/* Year Toggle */}
        <div className="flex gap-1 p-1 rounded-lg bg-slate-800 border border-slate-700">
          <button
            onClick={() => setSelectedYear('2025')}
            className={`px-4 py-2 text-sm rounded-md font-medium transition-all ${
              selectedYear === '2025'
                ? 'bg-slate-600 text-white shadow'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            2025
          </button>
          <button
            onClick={() => setSelectedYear('2026')}
            className={`px-4 py-2 text-sm rounded-md font-medium transition-all ${
              selectedYear === '2026'
                ? 'bg-[#2EC4B6] text-slate-900 shadow'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            2026 (Projected)
          </button>
        </div>
        
        {/* Period Toggle */}
        <div className="flex gap-1 p-1 rounded-lg bg-slate-800 border border-slate-700">
          <button
            onClick={() => setSelectedPeriod('monthly')}
            className={`px-4 py-2 text-sm rounded-md font-medium transition-all ${
              selectedPeriod === 'monthly'
                ? 'bg-slate-600 text-white shadow'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Month
          </button>
          <button
            onClick={() => setSelectedPeriod('quarterly')}
            className={`px-4 py-2 text-sm rounded-md font-medium transition-all ${
              selectedPeriod === 'quarterly'
                ? 'bg-slate-600 text-white shadow'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Quarter
          </button>
          <button
            onClick={() => setSelectedPeriod('annual')}
            className={`px-4 py-2 text-sm rounded-md font-medium transition-all ${
              selectedPeriod === 'annual'
                ? 'bg-slate-600 text-white shadow'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Annual
          </button>
        </div>
      </div>

      {/* Side by Side Panels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* LEFT PANEL - Non-Compliant Damage Exposure */}
        <div className="rounded-xl border border-red-900/50 bg-slate-800/50 overflow-hidden">
          {/* Panel Header */}
          <div className="px-5 py-4 border-b border-red-900/50 bg-red-950/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-500/20">
                  <ShieldAlert className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">Damage Risk</h2>
                  <p className="text-xs text-slate-400">Non-compliant rules</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-red-400">
                  {formatCurrency(damagesAmount)}
                  <span className="text-sm font-normal text-slate-500">/{getPeriodAbbrev(selectedPeriod)}</span>
                </p>
                <div className="flex items-center gap-1 justify-end text-xs text-slate-400">
                  <Users className="w-3 h-3" />
                  <span>{damages?.total_employees_at_risk || 0} at risk</span>
                </div>
              </div>
            </div>
            <p className="mt-2 text-xs text-slate-500">
              {damages?.rule_count || 0} rules • {showPenalties ? 'Including penalties' : 'Back wages only'}
            </p>
          </div>

          {/* Damages Rules List */}
          <div className="divide-y divide-slate-700/50 max-h-[500px] overflow-y-auto">
            {damagesRules.map((rule, idx) => {
              const isExpanded = expandedDamagesRules[rule.attestation_path_id];
              const yearData = rule[`damages_${selectedYear}`];
              const periodDivisor = selectedPeriod === 'annual' ? 1 : (selectedPeriod === 'quarterly' ? 4 : 12);
              const amount = showPenalties 
                ? (yearData?.total_damages_max || 0) / periodDivisor
                : (yearData?.back_wages || 0) / periodDivisor;
              const percentage = maxDamagesAmount > 0 ? (amount / maxDamagesAmount) * 100 : 0;

              return (
                <div key={rule.attestation_path_id} className="group">
                  <button
                    onClick={() => setExpandedDamagesRules(prev => ({ ...prev, [rule.attestation_path_id]: !prev[rule.attestation_path_id] }))}
                    className="w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-700/30 transition-colors"
                  >
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                      idx === 0 ? 'bg-red-500 text-white' : 'bg-slate-700 text-slate-400'
                    }`}>
                      {idx + 1}
                    </div>
                    
                    <div className="flex-1 min-w-0 text-left">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-white text-sm truncate">{rule.payrule}</p>
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-900/50 text-red-300">
                          {getViolationTypeLabel(rule.violation_type)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex-1 h-1 rounded-full bg-slate-700 overflow-hidden">
                          <div 
                            className="h-full bg-red-500 rounded-full transition-all duration-300"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <span className="text-xs text-slate-500">{rule.state_name}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="text-right">
                        <p className="font-bold text-red-400 text-sm">{formatCurrency(amount)}</p>
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <Users className="w-3 h-3" />
                          <span>{rule.employee_count}</span>
                        </div>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-slate-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      )}
                    </div>
                  </button>
                  
                  {isExpanded && (
                    <div className="px-4 pb-3 pt-1 bg-slate-900/50">
                      <div className="pl-9 space-y-3">
                        <div className="grid grid-cols-2 gap-2">
                          <div className="p-2 rounded-lg bg-slate-800/50 border border-slate-700">
                            <p className="text-[10px] text-slate-400">2025</p>
                            <p className="text-sm font-bold text-red-300">
                              {formatFullCurrency(showPenalties 
                                ? rule.damages_2025?.total_damages_max || 0
                                : rule.damages_2025?.back_wages || 0)}
                            </p>
                          </div>
                          <div className="p-2 rounded-lg bg-red-950/30 border border-red-800/50">
                            <p className="text-[10px] text-red-400">2026 (Projected)</p>
                            <p className="text-sm font-bold text-red-400">
                              {formatFullCurrency(showPenalties 
                                ? rule.damages_2026?.total_damages_max || 0
                                : rule.damages_2026?.back_wages || 0)}
                            </p>
                          </div>
                        </div>
                        
                        {rule.compliance_reason && (
                          <div className="p-2 rounded-lg bg-red-950/20 border border-red-800/30">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-red-400 mb-1">Why Non-Compliant</p>
                            <p className="text-xs text-slate-300">{rule.compliance_reason}</p>
                          </div>
                        )}
                        
                        <div className="flex gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); fetchDamagesDetail(rule.attestation_path_id); }}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-red-400 bg-red-900/30 hover:bg-red-900/50 rounded-lg transition-colors"
                          >
                            <DollarSign className="w-3.5 h-3.5" />
                            View Damages
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setSelectedRuleId(rule.attestation_path_id); }}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-[#2EC4B6] bg-[#2EC4B6]/10 hover:bg-[#2EC4B6]/20 rounded-lg transition-colors"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            View Rule
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            
            {damagesRules.length === 0 && (
              <div className="p-6 text-center text-slate-500 text-sm">
                No non-compliant rules found.
              </div>
            )}
          </div>
        </div>

        {/* RIGHT PANEL - Over-Compliant Savings */}
        <div className="rounded-xl border border-amber-900/50 bg-slate-800/50 overflow-hidden">
          {/* Panel Header */}
          <div className="px-5 py-4 border-b border-amber-900/50 bg-amber-950/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/20">
                  <TrendingUp className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">Overpayment</h2>
                  <p className="text-xs text-slate-400">Over-compliant rules</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-amber-400">
                  {formatCurrency(savingsAmount)}
                  <span className="text-sm font-normal text-slate-500">/{getPeriodAbbrev(selectedPeriod)}</span>
                </p>
                <div className="flex items-center gap-1 justify-end text-xs text-slate-400">
                  <Users className="w-3 h-3" />
                  <span>{cost_savings?.total_employees || 0} affected</span>
                </div>
              </div>
            </div>
            <p className="mt-2 text-xs text-slate-500">
              {cost_savings?.rule_count || 0} rules exceeding requirements
            </p>
          </div>

          {/* Savings Rules List */}
          <div className="divide-y divide-slate-700/50 max-h-[500px] overflow-y-auto">
            {savingsRules.map((rule, idx) => {
              const isExpanded = expandedSavingsRules[rule.attestation_path_id];
              const amount = rule[`savings_${selectedYear}_${selectedPeriod}`] || 0;
              const percentage = maxSavingsAmount > 0 ? (amount / maxSavingsAmount) * 100 : 0;

              return (
                <div key={rule.attestation_path_id} className="group">
                  <button
                    onClick={() => setExpandedSavingsRules(prev => ({ ...prev, [rule.attestation_path_id]: !prev[rule.attestation_path_id] }))}
                    className="w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-700/30 transition-colors"
                  >
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                      idx === 0 ? 'bg-amber-500 text-white' : 'bg-slate-700 text-slate-400'
                    }`}>
                      {idx + 1}
                    </div>
                    
                    <div className="flex-1 min-w-0 text-left">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-white text-sm truncate">{rule.payrule}</p>
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-900/50 text-amber-300">
                          {rule.over_compliance_type === 'meal_break' ? 'Meal Break' 
                            : rule.over_compliance_type === 'double_time' ? 'Double Time' 
                            : 'Overtime'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex-1 h-1 rounded-full bg-slate-700 overflow-hidden">
                          <div 
                            className="h-full bg-amber-500 rounded-full transition-all duration-300"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <span className="text-xs text-slate-500">{rule.state_name}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="text-right">
                        <p className="font-bold text-emerald-400 text-sm">{formatCurrency(amount)}</p>
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <Users className="w-3 h-3" />
                          <span>{rule.employee_count}</span>
                        </div>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-slate-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      )}
                    </div>
                  </button>
                  
                  {isExpanded && (
                    <div className="px-4 pb-3 pt-1 bg-slate-900/50">
                      <div className="pl-9 space-y-3">
                        <div className="grid grid-cols-2 gap-2">
                          <div className="p-2 rounded-lg bg-slate-800/50 border border-slate-700">
                            <p className="text-[10px] text-slate-400">2025</p>
                            <p className="text-sm font-bold text-amber-300">
                              {formatFullCurrency(rule.savings_2025_annual || 0)}
                            </p>
                          </div>
                          <div className="p-2 rounded-lg bg-emerald-950/30 border border-emerald-800/50">
                            <p className="text-[10px] text-emerald-400">2026 (Projected)</p>
                            <p className="text-sm font-bold text-emerald-400">
                              {formatFullCurrency(rule.savings_2026_annual || 0)}
                            </p>
                          </div>
                        </div>
                        
                        {rule.over_compliance_reason && (
                          <div className="p-2 rounded-lg bg-amber-950/20 border border-amber-800/30">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-400 mb-1">Why Over-Compliant</p>
                            <p className="text-xs text-slate-300">{rule.over_compliance_reason}</p>
                          </div>
                        )}
                        
                        <div className="flex gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); fetchSavingsDetail(rule.attestation_path_id); }}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-emerald-400 bg-emerald-900/30 hover:bg-emerald-900/50 rounded-lg transition-colors"
                          >
                            <DollarSign className="w-3.5 h-3.5" />
                            View Savings
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setSelectedRuleId(rule.attestation_path_id); }}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-[#2EC4B6] bg-[#2EC4B6]/10 hover:bg-[#2EC4B6]/20 rounded-lg transition-colors"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            View Rule
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            
            {savingsRules.length === 0 && (
              <div className="p-6 text-center text-slate-500 text-sm">
                No over-compliant rules found.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Insight Footer */}
      <div className="flex items-center gap-3 p-4 rounded-xl bg-slate-800/50 border border-slate-700">
        <Lightbulb className="w-5 h-5 text-[#2EC4B6] flex-shrink-0" />
        <p className="text-sm text-slate-300">
          Addressing <span className="text-red-400 font-semibold">{damages?.rule_count || 0} non-compliant rules</span> could 
          reduce exposure by <span className="text-red-400 font-semibold">{formatCurrency(damagesAmount)}/{getPeriodAbbrev(selectedPeriod)}</span>, 
          while fixing <span className="text-amber-400 font-semibold">{cost_savings?.rule_count || 0} over-compliant rules</span> could 
          save <span className="text-emerald-400 font-semibold">{formatCurrency(savingsAmount)}/{getPeriodAbbrev(selectedPeriod)}</span>
        </p>
      </div>

      {/* Modals */}
      {selectedRuleId && (
        <RuleDetailModal 
          ruleId={selectedRuleId} 
          onClose={() => setSelectedRuleId(null)} 
        />
      )}

      {savingsDetailRule && (
        <SavingsDetailModal
          savingsDetailData={savingsDetailData}
          savingsDetailLoading={savingsDetailLoading}
          selectedYear={selectedYear}
          setSelectedYear={setSelectedYear}
          selectedPeriod={selectedPeriod}
          setSelectedPeriod={setSelectedPeriod}
          onClose={() => {
            setSavingsDetailRule(null);
            setSavingsDetailData(null);
          }}
        />
      )}

      {damagesDetailRule && (
        <DamagesDetailModal
          damagesDetailData={damagesDetailData}
          damagesDetailLoading={damagesDetailLoading}
          selectedYear={selectedYear}
          setSelectedYear={setSelectedYear}
          selectedPeriod={selectedPeriod}
          setSelectedPeriod={setSelectedPeriod}
          onClose={() => {
            setDamagesDetailRule(null);
            setDamagesDetailData(null);
          }}
        />
      )}
    </div>
  );
};

export default InsightsPage;
